// The active org's nodes that no network view draws (app#388, G-1 follow-up of app#385).
//
// ⚠⚠ THE NODE'S TENANT IS `nodes.owner_org_id`, NEVER THE ORG OF A NETWORK IT IS IN
// (docs/PERMISSIONS.md § 2). The node list loads the active org's nodes by that column. But every
// view groups nodes by network, and the app loads only the active org's networks. So a node of the
// active org that sits in a network of ANOTHER org has no group, and no view drew it. A node in no
// network has none either. This module finds those nodes and says how to label and link them, and
// it makes the rows of the Nodes tab.
//
// ⚠ § 4 "see": the members of the node's org see a node. The org of the network sees it only
// later, with cross-org attach (§ 6). So these groups hold ONLY nodes whose tenant is the active
// org, never another org's node.
//
// Pure, so that `node --experimental-strip-types` can test it: the hook that uses it imports the
// stores, which pull in zustand and supabase.

import { nodeNameIn } from './nodeNetworks.ts'

type GroupableNode = {
  id: string
  name: string
  networkIds: readonly string[]
  ownerOrgId?: string | null
  networkNames?: Record<string, string>
}

/** The group of a node that no loaded network holds. */
export type OutsideGroup = 'other-org-network' | 'no-network'

export const OUTSIDE_GROUP_LABEL: Record<OutsideGroup, string> = {
  'other-org-network': "In other organizations' networks",
  'no-network': 'Not in a network',
}

/** The order of the two groups on the Nodes tab. Both come after the active org's networks. */
const GROUP_ORDER: readonly OutsideGroup[] = ['other-org-network', 'no-network']

/**
 * The nodes whose tenant is the active org and that are in no network, or in a network that is not
 * one of the loaded networks (the active org's). Each with its group.
 *
 * ⚠ A node in a loaded network AND in another org's network is returned too: it has a row in each
 * place, and only the row under its loaded network comes from `useMergedData`. A node has one
 * outside row, however many networks of other orgs it is in.
 *
 * ⚠ A node of another tenant is never returned, whatever its network. A node with no known tenant
 * is not returned either: it cannot be placed in the active org.
 *
 * ⚠⚠ `loadedNetworkIds` is `null` while the active org's networks are not known: before the first
 * read comes back, after an org switch, or after a read that failed. Then nothing is returned. The
 * nodes and the networks load in parallel, so without this test every node of the active org shows
 * in "In other organizations' networks" until the networks come back, and its page says "A network
 * of another organization".
 */
export const outsideNodes = <N extends GroupableNode>(
  nodes: readonly N[],
  loadedNetworkIds: ReadonlySet<string> | null,
  activeOrgId: string | null | undefined
): Array<{ node: N; group: OutsideGroup }> => {
  if (!activeOrgId || !loadedNetworkIds) return []
  const result: Array<{ node: N; group: OutsideGroup }> = []
  for (const node of nodes) {
    if (node.ownerOrgId !== activeOrgId) continue
    if (!node.networkIds.length) result.push({ node, group: 'no-network' })
    else if (node.networkIds.some(id => !loadedNetworkIds.has(id))) result.push({ node, group: 'other-org-network' })
  }
  return result.sort(
    (a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group) || a.node.name.localeCompare(b.node.name)
  )
}

/**
 * The node page of a node. One page, whatever networks the node is in.
 * ⚠ Never `/nodes/<id>/` with an empty segment: that matches no route, and the catch-all sends the
 * reader to the dashboard.
 */
export const nodePagePath = (nodeId: string): string => `/nodes/${nodeId}`

/**
 * The name of one of a node's networks. The app does not load a network of another org, so it
 * cannot print that network's name.
 */
export const nodeNetworkLabel = (
  networkName: string | null | undefined,
  group: OutsideGroup | undefined
): string => {
  if (networkName) return networkName
  if (group === 'other-org-network') return 'A network of another organization'
  return 'Network not found'
}

/** One row of the Nodes tab. A node has one row in each place that holds it. */
export type NodeRow<N, W> = {
  /** Unique in the list: a node in two networks has two rows. */
  key: string
  groupKey: string
  groupLabel: string
  node: N
  /** The node's name in this group. */
  name: string
  network: W | undefined
  /** The other places of this node, by name, for the `+1` mark. Empty for a node in one place. */
  others: string[]
}

type RowNetwork = { id?: string; name: string }

/**
 * The rows of the Nodes tab: one section per network by name, each sorted by node name, then the
 * two outside groups. `outside` is `outsideNodes`, in its order.
 *
 * ⚠ A node shows under EACH network it is in, with the name it has there (app#498). Only the `+1`
 * mark says that the row appears elsewhere too, so a row of a node in one place has none.
 *
 * ⚠ `nodes` is the nodes of `useMergedData`: each has at least one loaded network. A network it is
 * in that is not loaded has no section here. That row comes from `outside`.
 */
export const nodeRows = <N extends GroupableNode, W extends RowNetwork>(
  nodes: readonly N[],
  networks: readonly W[],
  outside: ReadonlyArray<{ node: N; group: OutsideGroup }>
): Array<NodeRow<N, W>> => {
  const byId = new Map(networks.flatMap(network => (network.id ? [[network.id, network] as const] : [])))
  const othersOf = (node: N, here: string | null) =>
    node.networkIds.filter(id => id !== here).map(id => nodeNetworkLabel(byId.get(id)?.name, 'other-org-network'))

  const rows: Array<NodeRow<N, W>> = nodes
    .flatMap(node =>
      node.networkIds.flatMap(id => {
        const network = byId.get(id)
        if (!network) return []
        return [
          {
            key: `${node.id}:${id}`,
            groupKey: id,
            groupLabel: network.name,
            node,
            name: nodeNameIn(node, id),
            network,
            others: othersOf(node, id),
          },
        ]
      })
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    // ⚠ Then by network id: two networks can have one name (`unique_network_name` is (owner_id,
    // name)), and their rows must not alternate. The sort is stable, so each group keeps its order.
    .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel) || a.groupKey.localeCompare(b.groupKey))
  // After the active org's networks: one group for its nodes in other orgs' networks, one for its
  // nodes in no network.
  for (const { node, group } of outside) {
    // The network of this row: the first one that is not loaded. The app has no name for it.
    const here = node.networkIds.find(id => !byId.has(id)) ?? null
    rows.push({
      key: `${node.id}:${group}`,
      groupKey: group,
      groupLabel: OUTSIDE_GROUP_LABEL[group],
      node,
      name: nodeNameIn(node, here),
      network: undefined,
      others: othersOf(node, here),
    })
  }
  return rows
}
